import { useState, useEffect } from 'react';

import type { APIUser } from 'Models/models';

import { ReactComponent as DefaultAvatar } from 'Components/UI_Library/Icons/Avatar.svg';
import Config from 'Config';
import classes from './Avatar.module.css';
import { checkImage } from './checkImage';
import { isDefined } from 'utils/typeguards/isDefined';

export type AvatarProps = {
    /** The user object */
    user?: Pick<APIUser, 'id' | 'firstName' | 'lastName'>;
    /** A cached version of the avatar URL */
    noCacheAvatar?: string;
    /** Extra custom CSS */
    style?: object;
    /** Custom sizing of the avatar (height and width) */
    size?: number | string;
    flat?: boolean;
};

const Avatar = ({ user, noCacheAvatar, style, size, flat }: AvatarProps) => {
    const [avatarExists, setAvatarExists] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const userId = user?.id;
    const userName = `${user?.firstName || ''} ${user?.lastName || ''}`;
    const initials = getInitials(userName);

    useEffect(() => {
        setAvatarExists(false);
        checkImage(`${Config.bucketUrl}${userId}/avatar.png`, (exists) => {
            if (exists) setAvatarExists(true);
        });

        setLoaded(true);
    }, [userId]);

    if (!loaded) return null;

    const renderAvatar = () => {
        if ((userId && avatarExists) || noCacheAvatar) {
            return (
                <img
                    className={`${classes.avatar} ${flat ? classes.flat : ''}`}
                    src={noCacheAvatar || `${Config.bucketUrl}${userId}/avatar.png`}
                    alt={userName}
                />
            );
        }

        if (initials) {
            return <div className={classes.noAvatar}>{initials}</div>;
        }

        return <DefaultAvatar />;
    };

    return (
        <div
            className={classes.avatarWrapper}
            style={{ ...style, ...(size && { height: `${size}px`, width: `${size}px` }) }}
        >
            {renderAvatar()}
        </div>
    );
};

const getInitials = (userName: string) =>
    userName?.split(' ').map(getInitialCharacter).filter(isDefined).join('') || ''; // eslint-disable-line disable-autofix/@typescript-eslint/no-unnecessary-condition -- optional chaining because don't trust input
const getInitialCharacter = (value: string) => value[0];

export default Avatar;
